import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';

/**
 * AuthGuard - Route protection and access control
 * 
 * Responsibilities:
 * - Protect routes from unauthenticated access
 * - Handle unregistered users (redirect to /unregistered)
 * - Redirect unauthenticated users to /login with returnUrl
 * - Make route access decisions based on AuthService state
 * 
 * Does NOT:
 * - Handle authentication logic (delegates to AuthService)
 * - Manage user state or sessions
 * - Display UI or handle user interactions
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  console.log('[AuthGuard] Checking access to:', state.url);
  
  const currentUser = authService.currentUser;
  const firebaseUser = authService.firebaseUser;
  
  // Allow access to unregistered page if Firebase user exists but no complete user profile
  if (state.url === '/unregistered') {
    if (firebaseUser) {
      console.log('[AuthGuard] Access granted to unregistered page');
      return true;
    } else {
      console.log('[AuthGuard] No Firebase user, redirecting to login');
      router.navigate(['/login']);
      return false;
    }
  }
  
  // Check if user is authenticated with Firebase but unregistered in our system
  if (currentUser && currentUser.firebaseAuth && currentUser.registered === false) {
    console.log('[AuthGuard] User needs registration, redirecting to setup');
    router.navigate(['/unregistered'], { queryParams: { returnUrl: state.url } });
    return false;
  }
  
  if (currentUser && currentUser.uid) {
    console.log('[AuthGuard] Access granted for authenticated user');
    
    // For now, allow access even without role to prevent redirect loops
    // In production, you might want to redirect to a setup page instead
    if (!currentUser.role) {
      console.warn('[AuthGuard] User has no role assigned');
    }
    
    return true;
  } else {
    console.log('[AuthGuard] Access denied, redirecting to login');
    
    router.navigate(['/login'], { 
      queryParams: { returnUrl: state.url }
    });
    return false;
  }
};